<template>
    <!-- Unified Custom PrimeVueDialog for Alert, Confirm, and Prompt -->
    <PrimeVueDialog
        v-model:visible="visible"
        class="global-dialog"
        :header="header"
        modal
        :draggable="false"
        dismissable-mask
        :style="{ width: '450px' }"
        :breakpoints="{ '640px': '92vw', '414px': '96vw' }"
        append-to="body"
        :focus-on-hide="false"
        :block-scroll="false"
        :pt="{
            mask: { class: 'global-dialog-mask' },
            title: {
                style: 'font-weight: 600; font-size: 1.125rem; padding-left: 0.5rem',
            },
        }"
        @after-hide="restoreFocus"
    >
        <div class="tw:pl-2">
            <div v-if="message" v-html="message" />

            <PrimeVueTextarea
                v-if="type === 'prompt'"
                v-bind="inputAttrs"
                v-model="inputValue"
                auto-resize
                :rows="3"
                class="tw:w-full tw:mt-2 tw:text-left"
                :class="{ 'p-invalid': validationError }"
                @input="validationError = false"
            />
            <small v-if="validationError" class="tw:text-red-500 tw:text-sm">Value is required.</small>
        </div>
        <template #footer>
            <PrimeVueButton
                v-if="secondaryText"
                :label="secondaryText"
                :class="secondaryButtonClass"
                class="p-button-text"
                @click="handleSecondary"
            />
            <PrimeVueButton
                v-if="showCancel"
                ref="cancelButton"
                :label="cancelText"
                class="p-button-text"
                @click="handleCancel"
            />
            <PrimeVueButton
                :label="confirmText"
                :badge="confirmBadge"
                :class="confirmButtonClass"
                @click="handleConfirm"
            />
        </template>
    </PrimeVueDialog>
</template>

<script>
import PrimeVueButton from 'primevue/button';
import PrimeVueDialog from 'primevue/dialog';
import PrimeVueTextarea from 'primevue/textarea';

export default {
    name: 'GlobalDialogs',
    components: { PrimeVueDialog, PrimeVueTextarea, PrimeVueButton },
    data() {
        return {
            visible: false,
            type: 'alert', // alert, confirm, prompt
            header: '',
            message: '',
            inputValue: '',
            inputAttrs: {},
            confirmText: 'OK',
            confirmBadge: null,
            cancelText: 'Cancel',
            confirmButtonClass: '',
            secondaryText: null,
            secondaryButtonClass: '',
            onConfirm: null,
            onCancel: null,
            onSecondary: null,
            focusRestoreEl: null,
            triggerElement: null,
            scrollPosition: 0,
            validationError: false,
            /** 'confirm' | 'cancel' | null — how a confirm dialog was closed (null = mask/X/Escape). */
            dialogDismissType: null,
        };
    },
    computed: {
        showCancel() {
            return this.type === 'confirm' || this.type === 'prompt';
        },
    },
    mounted() {
        window.addEventListener('primevue-dialog-request', this.handleRequest);
    },
    beforeUnmount() {
        window.removeEventListener('primevue-dialog-request', this.handleRequest);
    },
    methods: {
        handleRequest(event) {
            this.scrollPosition = window.scrollY;
            this.triggerElement = document.activeElement;
            const params = event.detail || {};
            this.type = params.type || 'alert';
            this.header = params.title || this.type.charAt(0).toUpperCase() + this.type.slice(1);
            this.message = params.message || '';
            this.inputValue = params.inputAttrs?.value || '';
            const { value, ...restAttrs } = params.inputAttrs || {};
            this.inputAttrs = restAttrs;
            this.confirmText = params.confirmText || 'OK';
            this.confirmBadge = params.confirmBadge ?? null;
            this.cancelText = params.cancelText || 'Cancel';
            this.confirmButtonClass = params.confirmButtonClass || '';
            this.onConfirm = params.onConfirm;
            this.onCancel = params.onCancel;
            this.secondaryText = params.secondaryText || null;
            this.secondaryButtonClass = params.secondaryButtonClass || '';
            this.onSecondary = params.onSecondary || null;
            this.focusRestoreEl = params.focusRestoreEl ?? null;
            this.dialogDismissType = null;
            this.visible = true;
            this.validationError = false;

            if (this.type === 'confirm') {
                this.$nextTick(() => {
                    const cancelBtn = this.$refs.cancelButton?.$el || this.$refs.cancelButton;
                    if (cancelBtn && typeof cancelBtn.focus === 'function') {
                        cancelBtn.focus();
                    }
                });
            }
        },
        handleConfirm() {
            if (this.type === 'prompt' && !this.inputValue) {
                this.validationError = true;
                return;
            }

            if (!this.visible) {
                return;
            }

            this.dialogDismissType = 'confirm';
            this.visible = false;

            if (this.onConfirm) {
                if (this.type === 'prompt') {
                    this.onConfirm(this.inputValue);
                } else {
                    this.onConfirm();
                }
            }
        },
        handleCancel() {
            this.dialogDismissType = 'cancel';
            if (this.onCancel) this.onCancel();
            this.visible = false;
        },
        handleSecondary() {
            this.dialogDismissType = 'cancel';
            if (this.onSecondary) this.onSecondary();
            this.visible = false;
        },
        restoreFocus() {
            if (this.type === 'confirm' && this.dialogDismissType === null && this.onCancel) {
                this.onCancel();
            }
            this.dialogDismissType = null;

            const savedScrollY = this.scrollPosition;
            const focusTarget =
                this.focusRestoreEl && document.contains(this.focusRestoreEl)
                    ? this.focusRestoreEl
                    : this.triggerElement;
            this.focusRestoreEl = null;
            this.$nextTick(() => {
                if (focusTarget && typeof focusTarget.focus === 'function') {
                    focusTarget.focus();
                }
                window.scrollTo(0, savedScrollY);
            });
        },
    },
};
</script>

<style>
/* Global dialog tweaks: make dialogs more phone-friendly */
.global-dialog {
    /* allow the breakpoints prop to control width, but ensure centered layout */
    margin: 0 auto;
}

/* Reduce inner content padding on small screens so the dialog uses more of the viewport */
@media (max-width: 640px) {
    .global-dialog .p-dialog-content {
        padding: 1rem !important;
    }

    .global-dialog .p-dialog-title {
        padding-left: 0.5rem !important;
        padding-right: 0.5rem !important;
    }

    .global-dialog .p-dialog-footer {
        padding: 0.75rem !important;
    }

    /* Mask padding applied via pt.mask class (p-dialog-mask wraps the dialog, not a child) */
    .global-dialog-mask {
        padding: 0 !important;
    }
}

/* Slightly tighter padding for very small phones */
@media (max-width: 414px) {
    .global-dialog .p-dialog-content {
        padding: 0.75rem !important;
    }
}
</style>
